import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { ColDef } from 'ag-grid-community'
import SidePanel from '@/components/ui/SidePanel'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'
import { HistoryStaffWorkloadService } from '@/api/generated/services/HistoryStaffWorkloadService'
import { HistoryActionService } from '@/api/generated/services/HistoryActionService'
import type { StaffWorkloadDetailResponse } from '@/api/generated/models/StaffWorkloadDetailResponse'
import type { StaffWorkloadTaskRow } from '@/api/generated/models/StaffWorkloadTaskRow'
import {
  type ActionHistoryItem,
  buildChangeColumnDefs,
  changeRangeCopyFormatter,
  flattenChangeRecords,
  useChangeValueFormatter,
  useUuidNameMaps
} from '@/pages/data-center/ChangeHistory/changeHistoryTable'

/**
 * 직원 업무 보유 현황 상세 패널
 *
 * 기본정보(이름·이메일·요약·이력) + 탭 3개.
 * - 과제 상세 / 날짜 연기 이력: GET /history/staff-workload/{member_id} 응답에 실려 온다.
 * - 변경이력: 양이 많아 GET /history/action?account_id=&workspace_id= 로 따로 페이지 로드한다(변경 이력 메뉴와 같은 표).
 */

interface Props {
  isOpen: boolean
  onClose: () => void
  memberId: string | null
  orgId: string | undefined
  workspaceId: string | undefined
}

type TabKey = 'tasks' | 'history' | 'postponements'

const ROW_HEIGHT = 42
const HEADER_HEIGHT = 40
const PAGE_SIZE = 20
// 헤더 + 행 + 페이지네이션 바 + 테두리 (DepartmentDetailPanel 과 같은 계산)
const GRID_HEIGHT = HEADER_HEIGHT + PAGE_SIZE * ROW_HEIGHT + 60 + 2
const HISTORY_PER_PAGE = 50

const font = designTokens.typography.fontFamily.sans[0]

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b flex items-center justify-between"
    style={{ borderBottomColor: designTokens.colors.gray[1], borderBottomWidth: '1px', padding: '7px 0px' }}
  >
    <h3 style={{ fontFamily: font, fontSize: '18px', fontWeight: 700, color: designTokens.colors.gray[6] }}>{title}</h3>
  </div>
)

const InfoField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center gap-5">
    <div className="w-37 flex-shrink-0">
      <span style={{ fontFamily: font, fontSize: '16px', fontWeight: 400, color: designTokens.colors.gray[6] }}>{label}</span>
    </div>
    <div className="flex-1 py-2">
      <span style={{ fontFamily: font, fontSize: '16px', fontWeight: 400, color: designTokens.colors.gray[6] }}>{value}</span>
    </div>
  </div>
)

const n = (v: number | undefined) => (v ?? 0).toLocaleString()

const taskColumnDefs: ColDef<StaffWorkloadTaskRow>[] = (
  [
    ['사업명', 'business_name', 180],
    ['전략과제명', 'task_group_name', 200],
    ['실행과제명', 'task_sub_group_name', 200],
    ['세부실행과제명', 'task_name', 240],
    ['수행업무명', 'task_item_name', 240]
  ] as const
).map(([headerName, field, width], i, arr) => ({
  headerName,
  field,
  width,
  minWidth: 140,
  ...(i === arr.length - 1 ? { flex: 1 } : {}),
  sortable: false,
  filter: false,
  valueGetter: (params: any) => params.data?.[field] || '-'
}))

const StaffWorkloadDetailPanel: React.FC<Props> = ({ isOpen, onClose, memberId, orgId, workspaceId }) => {
  const [detail, setDetail] = useState<StaffWorkloadDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('tasks')

  // 변경이력 탭: 누적 로딩
  const [histories, setHistories] = useState<ActionHistoryItem[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyPage, setHistoryPage] = useState(0)
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)

  const nameMaps = useUuidNameMaps(orgId)
  const formatChangeValue = useChangeValueFormatter(nameMaps)

  // 닫히면 상태 초기화 (다음에 다른 직원을 열 때 이전 데이터가 잠깐 보이지 않도록)
  useEffect(() => {
    if (!isOpen) {
      setDetail(null)
      setError(null)
      setActiveTab('tasks')
      setHistories([])
      setHistoryTotal(0)
      setHistoryPage(0)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !memberId || !orgId || !workspaceId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    HistoryStaffWorkloadService.appsHistoryViewsGetStaffWorkloadDetail({ memberId, orgId, workspaceId })
      .then(res => {
        if (!cancelled) setDetail(res)
      })
      .catch(err => {
        console.error('Failed to fetch staff workload detail:', err)
        if (!cancelled) setError('직원 상세를 불러오는데 실패했습니다.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, memberId, orgId, workspaceId])

  const fetchHistoryPage = useCallback(
    async (page: number) => {
      if (!detail || !workspaceId) return
      setIsHistoryLoading(true)
      try {
        const res = await HistoryActionService.appsHistoryViewsListActionHistories({
          accountId: detail.account_id,
          workspaceId,
          page,
          perPage: HISTORY_PER_PAGE
        })
        if (res?.data && res.meta) {
          const items = res.data as unknown as ActionHistoryItem[]
          setHistoryTotal(res.meta.total || 0)
          setHistories(prev => (page === 1 ? items : [...prev, ...items]))
          setHistoryPage(page)
        }
      } catch (err) {
        console.error('Failed to fetch staff change histories:', err)
      } finally {
        setIsHistoryLoading(false)
      }
    },
    [detail, workspaceId]
  )

  // 변경이력 탭을 처음 열 때 1페이지 로드
  useEffect(() => {
    if (activeTab === 'history' && detail && historyPage === 0) fetchHistoryPage(1)
  }, [activeTab, detail, historyPage, fetchHistoryPage])

  const historyRows = useMemo(() => flattenChangeRecords(histories), [histories])
  const postponementRows = useMemo(
    () => flattenChangeRecords((detail?.postponements ?? []) as unknown as ActionHistoryItem[]),
    [detail]
  )

  const historyColumnDefs = useMemo(
    () => buildChangeColumnDefs({ formatChangeValue, showUser: false }),
    [formatChangeValue]
  )
  const postponementColumnDefs = useMemo(
    () =>
      buildChangeColumnDefs({
        formatChangeValue,
        showUser: false,
        leading: [
          {
            headerName: '변경 일수',
            field: 'delay_days',
            width: 110,
            minWidth: 100,
            sortable: false,
            filter: false,
            cellClass: 'ag-right-aligned-cell',
            valueFormatter: (params: any) => (typeof params.value === 'number' ? `+${params.value}일` : '-')
          }
        ]
      }),
    [formatChangeValue]
  )

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'tasks', label: '과제 상세', count: detail?.tasks.length ?? 0 },
    { key: 'history', label: '변경이력', count: detail?.history_count ?? 0 },
    { key: 'postponements', label: '날짜 연기 이력', count: detail?.postponements.length ?? 0 }
  ]

  const summary = detail
    ? `사업 ${n(detail.business_count)}건 / 전략과제 ${n(detail.task_group_count)}건 / 실행과제 ${n(detail.task_sub_group_count)}건 / ` +
      `세부실행과제 ${n(detail.task_count)}건 / 수행업무 ${n(detail.task_item_count)}건`
    : '-'
  const historySummary = detail
    ? `변경이력 ${n(detail.history_count)}건 / 날짜 변경 ${n(detail.postpone_count)}건 (총 ${n(detail.postpone_days_total)}일 변경)`
    : '-'

  const gridCommon = {
    className: 'ag-theme-alpine',
    headerHeight: HEADER_HEIGHT,
    rowHeight: ROW_HEIGHT,
    height: GRID_HEIGHT,
    localeText: AG_GRID_LOCALE_KR,
    pagination: true,
    paginationPageSize: PAGE_SIZE,
    paginationPageSizeSelector: [PAGE_SIZE, 50, 100],
    enableRangeSelection: true,
    enableCellCopy: true,
    suppressRowClickSelection: true
  }

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={detail ? `${detail.account_name} 업무 보유 현황` : '업무 보유 현황'} width={1200}>
      <div className="flex flex-col space-y-6" style={{ padding: '24px' }}>
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-400">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* 기본정보 */}
        <div className="space-y-2">
          <SectionHeader title="기본정보" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField label="이름" value={detail?.account_name ?? (isLoading ? '불러오는 중...' : '-')} />
            <InfoField label="이메일" value={detail?.account_email ?? '-'} />
            <InfoField label="요약" value={summary} />
            <InfoField label="이력" value={historySummary} />
          </div>
        </div>

        {/* 탭 */}
        <div>
          <div className="flex items-center gap-1" style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}>
            {tabs.map(tab => {
              const isActive = tab.key === activeTab
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    fontFamily: font,
                    fontSize: '14px',
                    fontWeight: isActive ? 700 : 400,
                    color: isActive ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[5],
                    padding: '12px 16px',
                    borderBottom: `2px solid ${isActive ? designTokens.colors.primary.DEFAULT : 'transparent'}`,
                    marginBottom: '-1px',
                    cursor: 'pointer',
                    background: 'transparent'
                  }}
                >
                  {tab.label} ({tab.count.toLocaleString()})
                </button>
              )
            })}
          </div>

          <div className="mt-4 bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
            {activeTab === 'tasks' && (
              <DataGrid
                {...gridCommon}
                columnDefs={taskColumnDefs}
                rowData={detail?.tasks ?? []}
                loading={isLoading}
                getRowId={(params: any) => `${params.data.task_id}__${params.data.task_item_id ?? ''}__${params.data.task_sub_group_name ?? ''}`}
              />
            )}
            {activeTab === 'history' && (
              <>
                <DataGrid
                  {...gridCommon}
                  columnDefs={historyColumnDefs}
                  rowData={historyRows}
                  loading={isHistoryLoading && histories.length === 0}
                  getRowId={(params: any) => params.data.id}
                  rangeCopyFormatter={changeRangeCopyFormatter}
                />
                <div
                  className="flex items-center justify-center gap-4 py-3 border-t"
                  style={{ borderTopColor: designTokens.colors.gray[1] }}
                >
                  <span style={{ fontFamily: font, fontSize: '14px', color: designTokens.colors.gray[5] }}>
                    전체 {historyTotal.toLocaleString()}건 중 {histories.length.toLocaleString()}건 로드
                  </span>
                  {histories.length < historyTotal && (
                    <Button
                      size="small"
                      color="white"
                      onClick={() => fetchHistoryPage(historyPage + 1)}
                      disabled={isHistoryLoading}
                      className="flex-shrink-0"
                    >
                      {isHistoryLoading ? '불러오는 중...' : '더 보기'}
                    </Button>
                  )}
                </div>
              </>
            )}
            {activeTab === 'postponements' && (
              <DataGrid
                {...gridCommon}
                columnDefs={postponementColumnDefs}
                rowData={postponementRows}
                loading={isLoading}
                getRowId={(params: any) => params.data.id}
                rangeCopyFormatter={changeRangeCopyFormatter}
              />
            )}
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default StaffWorkloadDetailPanel
